/**
 * @description Number/BigInt 프로토타입 확장 및 숫자 관련 유틸리티 함수를 제공합니다.
 */

import isFinite from 'lodash-es/isFinite';
import type { Range } from './interface';

declare global {
  interface Number {
    /**
     * JavaScript 부동소수점 반올림 오차를 보정합니다.
     * @param prec - 정밀도 (기본값: 14)
     * @returns 보정된 숫자
     */
    correctFloat: (prec?: number) => number;

    /**
     * 숫자에 비율을 적용한 값을 반환합니다.
     * @param ratio - 적용할 비율 (예: 0.1 = 10% 증가)
     * @returns 비율이 적용된 값
     */
    applyRatio: (ratio: number) => number;

    /**
     * Intl.NumberFormat 옵션을 사용하여 문자열로 포맷합니다.
     * 포맷 객체가 캐싱되어 성능이 최적화됩니다.
     * @param options - Intl.NumberFormatOptions
     * @returns 포맷된 문자열
     */
    formatEx: (options: Intl.NumberFormatOptions) => string;

    /**
     * 숫자를 포맷된 문자열로 변환합니다.
     * @param maximumFractionDigits - 최대 소수점 자릿수 또는 'auto'
     * @param useGrouping - 천단위 구분자 사용 여부
     * @param signDisplay - 부호 표시 옵션
     * @returns 포맷된 문자열
     */
    format: (
      maximumFractionDigits?: number | 'auto',
      useGrouping?: boolean,
      signDisplay?: Intl.NumberFormatOptions['signDisplay'] | boolean,
    ) => string;

    /**
     * 고정 소수점 자릿수로 포맷합니다.
     * @param fractionDigits - 소수점 자릿수
     * @param useGrouping - 천단위 구분자 사용 여부
     * @param signDisplay - 부호 표시 옵션
     * @returns 포맷된 문자열
     */
    formatFixed: (
      fractionDigits: number,
      useGrouping?: boolean,
      signDisplay?: Intl.NumberFormatOptions['signDisplay'] | boolean,
    ) => string;
  }

  interface BigInt {
    /**
     * BigInt를 JSON으로 직렬화할 때 number로 변환합니다.
     * @returns number 값
     */
    toJSON(): number;
  }
}

/**
 * JavaScript 부동소수점 반올림 오차를 보정합니다.
 *
 * @example
 * ```ts
 * (0.1 + 0.2).correctFloat(); // 0.3 (0.30000000000000004 대신)
 * ```
 */
Number.prototype.correctFloat = function (prec?: number): number {
  return this.valueOf() > 1e14 ? this.valueOf() : parseFloat(this.toPrecision(prec || 14));
};

/**
 * 숫자에 비율을 적용한 값을 반환합니다.
 *
 * @example
 * ```ts
 * (100).applyRatio(0.1); // 110 (10% 증가)
 * (100).applyRatio(-0.2); // 80 (20% 감소)
 * ```
 */
Number.prototype.applyRatio = function (ratio: number): number {
  return this.valueOf() + this.valueOf() * ratio;
};

/**
 * Intl.NumberFormat 옵션으로 포맷합니다.
 * 포맷 객체가 전역 캐시에 저장되어 재사용됩니다.
 *
 * @example
 * ```ts
 * (1234.567).formatEx({ maximumFractionDigits: 2, useGrouping: true });
 * // '1,234.57'
 * ```
 */
Number.prototype.formatEx = function (options: Intl.NumberFormatOptions): string {
  if (isNaN(this.valueOf())) {
    return 'NaN';
  }
  if (this.valueOf() === Infinity) {
    return 'Infinity';
  }
  if (this.valueOf() === -Infinity) {
    return '-Infinity';
  }

  const key = JSON.stringify(options);
  if (!cache.get(key)) {
    cache.set(key, Intl.NumberFormat(undefined, options));
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (cache.get(key) as Intl.NumberFormat).format(this.valueOf());
};

/**
 * 숫자 크기에 따라 적절한 소수점 자릿수를 자동 결정합니다.
 * @internal
 * @param value - 대상 숫자
 * @returns 권장 소수점 자릿수
 */
function getAutoFractionDigits(value: number): number {
  const maximumFractionDigits =
    value < 1 ? 7 : value < 10 ? 6 : value < 100 ? 5 : value < 1000 ? 4 : value < 10000 ? 3 : 2;
  return maximumFractionDigits;
}

/**
 * 숫자를 포맷된 문자열로 변환합니다.
 *
 * @param data - 포맷할 숫자
 * @param maximumFractionDigits - 최대 소수점 자릿수 ('auto'면 숫자 크기에 따라 자동 결정)
 * @param useGrouping - 천단위 구분자 사용 여부 (기본값: true)
 * @param signDisplay - 부호 표시 옵션 (true='always', false='auto')
 * @returns 포맷된 문자열
 *
 * @example
 * ```ts
 * formatNumber(1234.5678, 2); // '1,234.57'
 * formatNumber(0.00012345, 'auto'); // '0.0001235'
 * formatNumber(100, 2, true, true); // '+100.00'
 * ```
 */
export function formatNumber(
  data: number,
  maximumFractionDigits: number | 'auto' = 'auto',
  useGrouping: boolean = true,
  signDisplay: Intl.NumberFormatOptions['signDisplay'] | boolean = 'auto',
): string {
  const newMaxFraction = maximumFractionDigits === 'auto' ? getAutoFractionDigits(data) : maximumFractionDigits;
  const newSignDisplay = signDisplay === true ? 'always' : signDisplay === false ? 'auto' : signDisplay;
  return data.formatEx({ maximumFractionDigits: newMaxFraction, useGrouping, signDisplay: newSignDisplay });
}

/**
 * 숫자를 포맷된 문자열로 변환합니다.
 *
 * @example
 * ```ts
 * (1234.5678).format(2); // '1,234.57'
 * (1234.5678).format('auto'); // '1,234.5678'
 * (-50).format(0, true, true); // '-50'
 * ```
 */
Number.prototype.format = function (
  maximumFractionDigits: number | 'auto' = 'auto',
  useGrouping: boolean = true,
  signDisplay: Intl.NumberFormatOptions['signDisplay'] | boolean = 'auto',
): string {
  return formatNumber(this.valueOf(), maximumFractionDigits, useGrouping, signDisplay);
};

/**
 * 고정 소수점 자릿수로 포맷합니다.
 *
 * @example
 * ```ts
 * (1234.5).formatFixed(2); // '1,234.50'
 * (1234.5678).formatFixed(2); // '1,234.57'
 * ```
 */
Number.prototype.formatFixed = function (
  fractionDigits: number,
  useGrouping: boolean = true,
  signDisplay: Intl.NumberFormatOptions['signDisplay'] | boolean = 'auto',
): string {
  return this.formatEx({
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    useGrouping: useGrouping,
    signDisplay: signDisplay === true ? 'always' : signDisplay === false ? 'auto' : signDisplay,
  });
};

/**
 * BigInt를 JSON으로 직렬화할 때 number로 변환합니다.
 * JSON.stringify()가 BigInt를 직접 지원하지 않기 때문에 필요합니다.
 *
 * @remarks
 * 큰 BigInt 값은 number로 변환 시 정밀도가 손실될 수 있습니다.
 */
BigInt.prototype.toJSON = function () {
  return Number(this);
};

/**
 * 값이 유효한 숫자인지 확인합니다.
 * NaN, undefined, null, Infinity, -Infinity, 문자열, 객체 등은 false를 반환합니다.
 *
 * @param value - 검사할 값
 * @returns 유효한 유한 숫자이면 true
 *
 * @example
 * ```ts
 * isValidNumber(123); // true
 * isValidNumber(0); // true
 * isValidNumber(NaN); // false
 * isValidNumber(Infinity); // false
 * isValidNumber('123'); // false
 * isValidNumber(null); // false
 * ```
 */
export function isValidNumber(value: unknown): boolean {
  return typeof value === 'number' && isFinite(value);
}

/**
 * 두 범위가 겹치거나 맞닿아 있는지 확인합니다.
 *
 * @param rangeA - 첫 번째 범위 [시작, 끝]
 * @param rangeB - 두 번째 범위 [시작, 끝]
 * @param includeAdjacent - 경계가 맞닿은 경우도 겹침으로 처리할지 여부
 * @returns 범위가 겹치면 true
 *
 * @example
 * ```ts
 * isOverlappedOrAdjacent([1, 5], [3, 7]); // true (겹침)
 * isOverlappedOrAdjacent([1, 5], [5, 10]); // false (맞닿음만)
 * isOverlappedOrAdjacent([1, 5], [5, 10], true); // true (맞닿음 포함)
 * isOverlappedOrAdjacent([1, 5], [6, 10]); // false
 * ```
 */
export function isOverlappedOrAdjacent(rangeA: Range, rangeB: Range, includeAdjacent: boolean = false): boolean {
  const [aStart, aEnd] = rangeA;
  const [bStart, bEnd] = rangeB;

  if (includeAdjacent) {
    return Math.max(aStart, bStart) <= Math.min(aEnd, bEnd);
  }
  return Math.max(aStart, bStart) < Math.min(aEnd, bEnd);
}

export {};
